import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney } from '../lib/format';
import type { WorkPlan, WorkPlanItem } from '../lib/types';
import { ErrorState, SkeletonRows, useToast } from '../components/ui';
import { IconAlert, IconCash, IconChecklist, IconDownload, IconFile, IconInbox, IconMerge, IconRepeat, IconTarget, IconTruck } from '../components/icons';

const KIND_ICON: Record<WorkPlanItem['kind'], typeof IconCash> = {
  review: IconInbox,
  collect: IconCash,
  followup: IconRepeat,
  quote: IconFile,
  billing: IconTruck,
  duplicates: IconMerge,
  goal: IconTarget,
  unlinked: IconAlert,
  dormant: IconRepeat,
  backup: IconDownload,
};

export const useWorkPlan = (enabled = true) => useQuery({ queryKey: ['workplan'], queryFn: () => api<WorkPlan>('/api/workplan'), enabled });

export function WorkPlanList({ items, limit }: { items: WorkPlanItem[]; limit?: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const toggle = async (i: WorkPlanItem, done: boolean) => {
    // Optimistic: the tick shows at once, the server confirms.
    qc.setQueryData<WorkPlan>(['workplan'], (old) =>
      old ? { ...old, [i.section]: old[i.section].map((x) => (x.key === i.key ? { ...x, done } : x)) } : old,
    );
    try {
      await api('/api/workplan/done', { body: { key: i.key, section: i.section, done } });
    } catch (e) {
      toast.error(e);
      qc.invalidateQueries({ queryKey: ['workplan'] });
    }
  };
  const shown = limit ? items.filter((i) => !i.done).slice(0, limit) : items;
  if (!shown.length) return <div className="empty" style={{ padding: 22 }}><p>{limit ? 'הכל בוצע — כל הכבוד.' : 'אין משימות.'}</p></div>;
  return (
    <ul className="plan-list">
      {shown.map((i) => {
        const Icon = KIND_ICON[i.kind];
        return (
          <li key={i.key} className={`plan-item ${i.done ? 'done' : ''}`}>
            <input type="checkbox" checked={Boolean(i.done)} onChange={(e) => toggle(i, e.target.checked)} aria-label={`בוצע: ${i.title}`} />
            <span className={`plan-kind ${i.kind}`}><Icon size={14} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={i.link} className="plan-title strong" style={{ color: 'var(--ink)' }}>{i.title}</Link>
              {i.detail ? <div className="small muted">{i.detail}</div> : null}
            </div>
            {i.amount != null && i.kind !== 'followup' ? <span className="tabular small nowrap">{fmtMoney(i.amount, { round: true })}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

export function WorkPlanPage() {
  const { data, isLoading, error, refetch } = useWorkPlan();
  const doneDaily = data?.daily.filter((i) => i.done).length ?? 0;
  const doneWeekly = data?.weekly.filter((i) => i.done).length ?? 0;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>סדר עבודה</h1>
          <div className="sub">מה כדאי לעשות היום והשבוע, לפי הנתונים — גבייה קודם, אחריה מכירות. סימון "בוצע" נשמר ליום (או לשבוע).</div>
        </div>
      </div>
      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : (
        <div className="grid-2">
          <section className="panel">
            <div className="panel-head">
              <IconChecklist />
              <h2>היום</h2>
              <span className="small muted">{data ? fmtDate(data.today) : ''}</span>
              <span className="spacer" />
              {data ? <span className="small muted">{doneDaily}/{data.daily.length} בוצעו</span> : null}
            </div>
            {isLoading ? <table className="table"><tbody><SkeletonRows rows={6} cols={2} /></tbody></table> : <WorkPlanList items={data?.daily ?? []} />}
          </section>
          <section className="panel">
            <div className="panel-head">
              <IconChecklist />
              <h2>השבוע</h2>
              <span className="small muted">{data ? `מיום ראשון ${fmtDate(data.weekStart)}` : ''}</span>
              <span className="spacer" />
              {data ? <span className="small muted">{doneWeekly}/{data.weekly.length} בוצעו</span> : null}
            </div>
            {isLoading ? <table className="table"><tbody><SkeletonRows rows={5} cols={2} /></tbody></table> : <WorkPlanList items={data?.weekly ?? []} />}
          </section>
        </div>
      )}
    </>
  );
}
